import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClaveApi, UsoClaveApi, VentanaUso } from '../../api/ops/clavesApi';
import type { PermisoCatalogo } from '../../api/ops/equipo';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Fecha } from '../../componentes/Fecha';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { hayClavesDePruebas, leerConfiguracion } from '../../config';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { cifra, diaMes, diaYHora } from '../../logica/fechasCortas';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import {
    CLAVE_ESTADO,
    direccionApi,
    esCerrada,
    gruposDePermisos,
    inicioDeVentana,
    ipsValidas,
    nombreModulo,
    SOLAPAMIENTOS,
    subtituloDelUso,
    textoRevocada,
    TIPOS_CLAVE,
    type GrupoPermisos,
} from './reglasClaves';
import { sinProtocolo } from './reglas';

/**
 * «Para desarrolladores» (13.6), plegado al final de Widgets (`?apartado=api` lo abre y baja hasta él): las claves
 * privadas y de pruebas del negocio (las públicas de la web y los widgets se gestionan solas), con filtro Activas ·
 * Revocadas y caducadas y «Nueva clave»; la dirección de la API v1. Solo con `api.claves.gestionar` (Propietario y
 * Administrador). La clave se enseña **una sola vez** (`secret` de createApiKey y rotateApiKey). Ficha con Uso (por horas
 * o por días, 24 horas o 14 días en la zona del negocio, G643) y Permisos; rotar (24 h, 3 días, 7 días o nada) y revocar
 * (también en solo lectura; «Revocada el…», G639). «Abrir la documentación» va a `VITE_API_DOCS_URL` (G642).
 *
 * Los permisos que se ofrecen son los efectivos de quien la crea (G37, G612) con su texto de `listPermissions` cuando se
 * puede leer (si no, el código).
 */
interface Datos {
    claves: ClaveApi[];
    nombres: Record<string, string>;
    catalogo: PermisoCatalogo[] | null;
}

interface Secreto {
    nombre: string;
    secreto: string;
    rotada: boolean;
    finAntigua: string | null;
}

export function ParaDesarrolladores(props: PropsPantalla & { abrirAlEntrar: boolean }) {
    const { ops, negocio, movil, soloLectura, abrirAlEntrar } = props;
    const { puede, codigos } = usePermisos();
    const avisar = useAvisoBreve();
    const [abierto, setAbierto] = useState(abrirAlEntrar);
    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [filtro, setFiltro] = useState(0);
    const [nueva, setNueva] = useState(false);
    const [ficha, setFicha] = useState<string | null>(null);
    const [secreto, setSecreto] = useState<Secreto | null>(null);
    const caja = useRef<HTMLElement>(null);
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    useEffect(() => {
        if (abrirAlEntrar) {
            try {
                caja.current?.scrollIntoView({ block: 'start' });
            } catch {
                // Sin desplazamiento (navegador sin scrollIntoView): la sección sigue ahí debajo.
            }
        }
    }, [abrirAlEntrar]);

    useEffect(() => {
        if (!abierto) {
            return;
        }
        let vivo = true;
        Promise.all([
            ops.listApiKeys(negocio, { limit: 100 }),
            puede('equipo.miembros.ver') ? ops.listMembers(negocio, { limit: 100 }).catch(() => null) : Promise.resolve(null),
            puede('equipo.roles.gestionar') ? ops.listPermissions(negocio).catch(() => null) : Promise.resolve(null),
        ])
            .then(([claves, miembros, catalogo]) => {
                if (!vivo) {
                    return;
                }
                setDatos({
                    claves: claves.data.filter((c) => c.type === 'private' || c.type === 'test'),
                    nombres: Object.fromEntries((miembros?.data ?? []).map((m) => [m.id, m.display_name])),
                    catalogo: catalogo?.data ?? null,
                });
                setError(null);
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [abierto, ops, negocio, puede, intento]);

    const activas = datos?.claves.filter((c) => !esCerrada(c)) ?? [];
    const cerradas = datos?.claves.filter((c) => esCerrada(c)) ?? [];
    const visibles = filtro === 0 ? activas : cerradas;
    const resumen = datos === null ? 'Claves de API y la API v1' : `${String(activas.length)} ${activas.length === 1 ? 'clave' : 'claves'} · ${String(datos.claves.filter((c) => c.status === 'active').length)} ${datos.claves.filter((c) => c.status === 'active').length === 1 ? 'activa' : 'activas'} · API v1`;
    const grupos = gruposDePermisos(codigos(), datos?.catalogo ?? null);
    const elegida = datos?.claves.find((c) => c.id === ficha) ?? null;
    const autor = (c: ClaveApi) => (c.created_by_member_id == null ? 'Reservaria' : (datos?.nombres[c.created_by_member_id] ?? 'Alguien del equipo'));
    const configuracion = leerConfiguracion();
    const direccion = direccionApi(configuracion.api, window.location.origin);
    // G642: la dirección de la documentación (api/docs) llega por configuración; sin ella, no hay botón.
    const documentacion = configuracion.documentacionApi;

    const copiar = (texto: string, hecho: string) => {
        const portapapeles = navigator.clipboard as Clipboard | undefined;
        void portapapeles?.writeText(texto).then(() => { avisar({ tipo: 'ok', texto: hecho }); });
    };

    return (
        <section ref={caja} className="rv-wb-dev" aria-label="Para desarrolladores">
            <button type="button" className="rv-wb-plegable" aria-expanded={abierto} onClick={() => { setAbierto((a) => !a); }}>
                <span className="rv-ca-baldosa">
                    <Icono nombre="code" />
                </span>
                <span className="rv-ca-fila__texto">
                    <strong>Para desarrolladores</strong>
                    <span>{resumen}</span>
                </span>
                <Icono nombre={abierto ? 'chevron-up' : 'chevron-down'} />
            </button>
            {abierto &&
                (error !== null ? (
                    <EstadoError seccion="las claves de API" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />
                ) : datos === null ? (
                    <EstadoCargando seccion="las claves de API" />
                ) : (
                    <div className="rv-wb-form">
                        <div className="rv-wb-dev__barra">
                            <Filtros etiqueta="Claves" items={[{ etiqueta: 'Activas', cifra: activas.length }, { etiqueta: 'Revocadas y caducadas', cifra: cerradas.length }]} valor={filtro} onCambio={setFiltro} />
                            {!soloLectura && (
                                <Boton tipo="secundario" icono="plus" movil={movil} onClick={() => { setNueva(true); }}>
                                    Nueva clave
                                </Boton>
                            )}
                        </div>
                        {soloLectura && <p className="rv-ca-nota rv-ca-nota--gris">El negocio está en solo lectura: no se crean ni se rotan claves. Revocar sí se puede.</p>}
                        {visibles.length === 0 ? (
                            <div className="rv-wb-vacia">
                                <Icono nombre="key-round" />
                                <strong>{filtro === 1 && datos.claves.length > 0 ? 'Ninguna clave revocada ni caducada.' : 'Todavía no hay claves de API'}</strong>
                                <span>{filtro === 1 && datos.claves.length > 0 ? 'Las que revoques o caduquen se quedan aquí, con su uso.' : 'Crea una para que un sistema tuyo o de un integrador trabaje con tus reservas, citas o pedidos.'}</span>
                                {!(filtro === 1 && datos.claves.length > 0) && !soloLectura && (
                                    <Boton icono="plus" movil={movil} onClick={() => { setNueva(true); }}>
                                        Nueva clave
                                    </Boton>
                                )}
                            </div>
                        ) : (
                            <Tabla
                                etiqueta="Claves de API"
                                movil={movil}
                                columnas={[
                                    { clave: 'clave', titulo: 'Clave', ancho: 'minmax(0, 1.5fr)' },
                                    { clave: 'permisos', titulo: 'Permisos', ancho: 'minmax(0, 1.1fr)' },
                                    { clave: 'uso', titulo: 'Último uso', ancho: 'minmax(0, 1fr)' },
                                    { clave: 'estado', titulo: 'Estado', ancho: '120px' },
                                ]}
                                filas={visibles.map((c) => {
                                    const [linea1, linea2] = lineasDeUso(c, autor(c));
                                    const estado = CLAVE_ESTADO[c.status];
                                    const n = c.permissions.length;
                                    return {
                                        clave: c.id,
                                        etiqueta: c.name,
                                        onAbrir: () => { setFicha(c.id); },
                                        celdas: {
                                            clave: (
                                                <span className="rv-en-celda">
                                                    <strong>{c.name}</strong>
                                                    <small>
                                                        <span className="rv-wb-mono">{`${c.prefix}…`}</span>
                                                        {` · ${c.type === 'test' ? 'De pruebas' : 'Privada'}`}
                                                    </small>
                                                </span>
                                            ),
                                            permisos: (
                                                <span className="rv-en-celda">
                                                    <strong>{`${String(n)} ${n === 1 ? 'permiso' : 'permisos'}`}</strong>
                                                    <small>{[...new Set(c.permissions.map((p) => nombreModulo(p, datos.catalogo)))].join(', ')}</small>
                                                </span>
                                            ),
                                            uso: (
                                                <span className="rv-en-celda">
                                                    <strong>{linea1}</strong>
                                                    <small>{linea2}</small>
                                                </span>
                                            ),
                                            estado: c.status === 'active' ? '' : <Chip estado={estado.chip}>{estado.texto}</Chip>,
                                        },
                                    };
                                })}
                            />
                        )}
                        <p className="rv-ca-nota rv-ca-nota--gris">Las claves de tus widgets no salen aquí: se crean y se revocan solas.</p>
                        <div className="rv-wb-api">
                            <span className="rv-ca-baldosa rv-wb-api__baldosa">
                                <Icono nombre="book-open" />
                            </span>
                            <span className="rv-ca-fila__texto">
                                <strong>API de Reservaria v1</strong>
                                <span>{hayClavesDePruebas(configuracion) ? 'Para conectar un sistema tuyo o de un integrador. Prueba antes con una clave de pruebas.' : 'Para conectar un sistema tuyo o de un integrador.'}</span>
                                <span className="rv-wb-botones">
                                    <span className="rv-wb-mono rv-wb-api__url">{sinProtocolo(direccion)}</span>
                                    <Boton tipo="secundario" icono="copy" onClick={() => { copiar(direccion, 'Copiado'); }}>
                                        Copiar
                                    </Boton>
                                    {documentacion !== '' && (
                                        <Boton tipo="secundario" icono="external-link" onClick={() => { window.open(documentacion, '_blank', 'noopener'); }}>
                                            Abrir la documentación
                                        </Boton>
                                    )}
                                </span>
                            </span>
                        </div>
                    </div>
                ))}
            {nueva && (
                <NuevaClave
                    {...props}
                    grupos={grupos}
                    onCerrar={() => { setNueva(false); }}
                    onCreada={(c, s) => {
                        setNueva(false);
                        setFiltro(0);
                        setSecreto({ nombre: c.name, secreto: s, rotada: false, finAntigua: null });
                        recargar();
                    }}
                />
            )}
            {elegida !== null && (
                <FichaClave
                    key={elegida.id}
                    {...props}
                    clave={elegida}
                    autor={autor(elegida)}
                    catalogo={datos?.catalogo ?? null}
                    onCerrar={() => { setFicha(null); }}
                    onRotada={(nuevaClave, s, finAntigua) => {
                        setFicha(null);
                        setSecreto({ nombre: nuevaClave.name, secreto: s, rotada: true, finAntigua });
                        recargar();
                    }}
                    onRevocada={() => {
                        setFicha(null);
                        avisar({ tipo: 'ok', texto: `«${elegida.name}» (${elegida.prefix}…) revocada: ya no funciona.` });
                        recargar();
                    }}
                />
            )}
            <Ventana
                abierta={secreto !== null}
                titulo="Copia la clave ahora"
                texto={secreto === null ? '' : secreto.rotada ? `Esta es la clave nueva de «${secreto.nombre}». Cámbiala en el sistema que la usa.` : `Esta es la clave «${secreto.nombre}». Guárdala en un sitio seguro y dásela solo a quien la vaya a usar.`}
                accion="Ya la he guardado"
                accionIcono="check"
                cancelar=""
                movil={movil}
                onAccion={() => {
                    if (secreto !== null) {
                        avisar({ tipo: 'ok', texto: secreto.rotada ? `Clave nueva de «${secreto.nombre}» creada. La antigua está en rotación.` : `Clave «${secreto.nombre}» creada.` });
                    }
                    setSecreto(null);
                }}
                onCerrar={() => undefined}
            >
                {secreto !== null && (
                    <div className="rv-wb-form">
                        <p className="rv-wb-banda rv-wb-banda--roja" role="alert">
                            <Icono nombre="eye-off" />
                            <span>
                                <strong>Es la única vez que la verás.</strong> No la pegues nunca en una web ni en un widget: es solo para servidores.
                            </span>
                        </p>
                        <div className="rv-wb-secreto">
                            <pre className="rv-wb-codigo" aria-label="Clave secreta">
                                {secreto.secreto}
                            </pre>
                            <Boton tipo="secundario" icono="copy" onClick={() => { copiar(secreto.secreto, 'Clave copiada'); }}>
                                Copiar la clave
                            </Boton>
                        </div>
                        <p className="rv-ca-nota rv-ca-nota--gris">
                            {secreto.rotada && secreto.finAntigua !== null ? `La antigua sigue funcionando hasta ${secreto.finAntigua}. Si pierdes esta, vuelve a rotar.` : 'Si la pierdes, rota la clave: se crea otra con los mismos permisos.'}
                        </p>
                    </div>
                )}
            </Ventana>
        </section>
    );
}

/** Las dos líneas de «Último uso» de la tabla. */
function lineasDeUso(c: ClaveApi, autor: string): [string, string] {
    const primera =
        c.status === 'revoked' ? textoRevocada(c) : c.status === 'expired' ? `Caducó el ${diaMes(c.expires_at)}` : c.last_used_at == null ? 'Sin usar todavía' : `Usada ${diaYHora(c.last_used_at).toLocaleLowerCase('es')}`;
    const segunda =
        c.status === 'rotating' ? `Deja de funcionar ${diaYHora(c.expires_at).toLocaleLowerCase('es')}` : c.status === 'active' && c.expires_at != null ? `Caduca el ${diaMes(c.expires_at)}` : `Creada el ${diaMes(c.created_at)} · ${autor}`;
    return [primera, segunda];
}

function NuevaClave({ ops, negocio, movil, grupos, onCerrar, onCreada }: PropsPantalla & { grupos: readonly GrupoPermisos[]; onCerrar: () => void; onCreada: (c: ClaveApi, secreto: string) => void }) {
    const avisar = useAvisoBreve();
    const [tipo, setTipo] = useState<'private' | 'test'>('private');
    const [nombre, setNombre] = useState('');
    const [elegidos, setElegidos] = useState<string[]>([]);
    const [conIps, setConIps] = useState(false);
    const [ips, setIps] = useState('');
    const [conCaducidad, setConCaducidad] = useState(false);
    const [caduca, setCaduca] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [clave] = useState(() => globalThis.crypto.randomUUID());
    // G731: en producción no se ofrece «Clave de pruebas» (la API respondería 422).
    const tipos = hayClavesDePruebas(leerConfiguracion()) ? TIPOS_CLAVE : TIPOS_CLAVE.filter((t) => t.valor !== 'test');

    const listaIps = ips.split(/\s+/).filter(Boolean);
    const errores = {
        nombre: nombre.trim() === '' ? 'Ponle un nombre.' : null,
        permisos: elegidos.length === 0,
        ips: conIps && !ipsValidas(listaIps) ? 'Alguna dirección no es válida.' : null,
    };
    const valida = errores.nombre === null && !errores.permisos && errores.ips === null;

    const crear = () => {
        if (!valida) {
            setIntentado(true);
            return;
        }
        setOcupado(true);
        ops.createApiKey(
            negocio,
            {
                type: tipo,
                name: nombre.trim(),
                permissions: elegidos,
                ...(conIps && listaIps.length > 0 ? { allowed_ips: listaIps } : {}),
                ...(conCaducidad && caduca !== '' ? { expires_at: new Date(`${caduca}T00:00:00`).toISOString() } : {}),
            },
            clave,
        )
            .then((c) => { onCreada(c, c.secret ?? ''); })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido crear la clave.' }); })
            .finally(() => { setOcupado(false); });
    };

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo={nombre.trim() === '' ? 'Nueva clave de API' : nombre.trim()}
            eyebrow={tipo === 'test' ? 'Nueva clave de pruebas' : 'Nueva clave privada'}
            subtitulo="Se enseña una sola vez, al crearla."
            icono="key-round"
            accion="Crear clave"
            accionIcono="key-round"
            ocupado={ocupado}
            ocupadoTexto="Creando…"
            onAccion={crear}
        >
            <div className="rv-wb-form">
                <h3 className="rv-wb-ceja">Para qué es</h3>
                <div role="radiogroup" aria-label="Tipo de clave" className="rv-wb-form">
                    {tipos.map((t) => (
                        <OpcionRadio key={t.valor} elegida={tipo === t.valor} onElegir={() => { setTipo(t.valor); }} titulo={t.titulo} texto={t.texto} />
                    ))}
                </div>
                <Campo etiqueta="Nombre de la clave" valor={nombre} onChange={setNombre} max={80} error={intentado ? errores.nombre : null} ayuda="Quién o qué la usa, para reconocerla." nombre="clave-nombre" />
                <div className="rv-wb-dev__permisos">
                    <div className="rv-wb-dev__titulo">
                        <span>
                            <h3 className="rv-wb-ceja">Permisos</h3>
                            <small>Solo los que necesite. Nunca más que los tuyos.</small>
                        </span>
                        <strong>{`${String(elegidos.length)} ${elegidos.length === 1 ? 'elegido' : 'elegidos'}`}</strong>
                    </div>
                    {grupos.map((g) => (
                        <div key={g.modulo} className="rv-wb-dev__grupo">
                            <strong>{g.nombre}</strong>
                            {g.permisos.map((p) => (
                                <Interruptor
                                    key={p.codigo}
                                    etiqueta={p.texto}
                                    valor={elegidos.includes(p.codigo)}
                                    compacto
                                    linea="ninguna"
                                    onChange={(v) => { setElegidos((a) => (v ? [...a, p.codigo] : a.filter((x) => x !== p.codigo))); }}
                                />
                            ))}
                        </div>
                    ))}
                    {intentado && errores.permisos && (
                        <span role="alert" className="rv-wb-dev__error">
                            <Icono nombre="circle-alert" />
                            Elige al menos un permiso.
                        </span>
                    )}
                </div>
                {conIps ? (
                    <Campo etiqueta="Direcciones IP permitidas" valor={ips} onChange={setIps} filas={3} error={intentado ? errores.ips : null} ayuda="Una por línea. Desde otras, se rechaza." nombre="clave-ips" />
                ) : (
                    <Boton tipo="fantasma" icono="plus" onClick={() => { setConIps(true); }}>
                        Limitar a unas direcciones IP
                    </Boton>
                )}
                {conCaducidad ? (
                    <Fecha etiqueta="Caduca el" valor={caduca} onChange={setCaduca} min={new Date().toISOString().slice(0, 10)} ayuda="Ese día deja de funcionar sola." nombre="clave-caduca" />
                ) : (
                    <Boton tipo="fantasma" icono="plus" onClick={() => { setConCaducidad(true); }}>
                        Ponerle fecha de caducidad
                    </Boton>
                )}
                <p className="rv-wb-banda" role="note">
                    <Icono nombre="eye-off" />
                    <span>{tipo === 'test' ? 'La verás una sola vez, al crearla. Funciona solo contra el negocio de demostración.' : 'La verás una sola vez, al crearla. Es secreta: solo para servidores, nunca en una web ni en un widget.'}</span>
                </p>
            </div>
        </PanelLateral>
    );
}

const PESTANAS = [
    { texto: 'Uso', icono: 'chart-column' },
    { texto: 'Permisos', icono: 'shield' },
] as const;

function FichaClave({
    ops,
    negocio,
    movil,
    soloLectura,
    clave,
    autor,
    catalogo,
    onCerrar,
    onRotada,
    onRevocada,
}: PropsPantalla & { clave: ClaveApi; autor: string; catalogo: PermisoCatalogo[] | null; onCerrar: () => void; onRotada: (c: ClaveApi, secreto: string, finAntigua: string | null) => void; onRevocada: () => void }) {
    const avisar = useAvisoBreve();
    const [pestana, setPestana] = useState(0);
    const [ventana, setVentana] = useState<VentanaUso>('hour');
    const [uso, setUso] = useState<{ ventana: VentanaUso; uso: UsoClaveApi } | null>(null);
    const [modal, setModal] = useState<'rotar' | 'revocar' | null>(null);
    const [solape, setSolape] = useState('24');
    const [ocupado, setOcupado] = useState(false);
    const [claveRotar] = useState(() => globalThis.crypto.randomUUID());
    const cerrada = esCerrada(clave);
    const estado = CLAVE_ESTADO[clave.status];

    useEffect(() => {
        let vivo = true;
        ops.getApiKeyUsage(negocio, clave.id, ventana)
            .then((u) => {
                if (vivo) {
                    setUso({ ventana, uso: u });
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, clave.id, ventana]);

    const rotar = () => {
        setOcupado(true);
        const horas = Number(solape);
        ops.rotateApiKey(negocio, clave.id, horas, claveRotar)
            .then((c) => { onRotada(c, c.secret ?? '', horas === 0 ? null : diaYHora(new Date(Date.now() + horas * 3_600_000).toISOString()).toLocaleLowerCase('es')); })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido rotar la clave.' }); })
            .finally(() => { setOcupado(false); setModal(null); });
    };
    const revocar = () => {
        setOcupado(true);
        ops.revokeApiKey(negocio, clave.id)
            .then(() => { onRevocada(); })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido revocar la clave.' }); })
            .finally(() => { setOcupado(false); setModal(null); });
    };

    const u = uso?.ventana === ventana ? uso.uso : null;
    const peticiones = u?.windows.reduce((t, w) => t + w.requests, 0) ?? 0;
    const frenadas = u?.windows.reduce((t, w) => t + w.rate_limited, 0) ?? 0;
    const maximo = Math.max(1, ...(u?.windows.map((w) => w.requests) ?? []));
    const grupos = gruposDePermisos(clave.permissions, catalogo);
    const aviso =
        clave.status === 'rotating'
            ? `En rotación: sigue funcionando hasta ${diaYHora(clave.expires_at).toLocaleLowerCase('es')}. Después se revoca sola. La nueva ya está en la lista.`
            : clave.status === 'revoked'
              ? `${textoRevocada(clave)}: ya no funciona. Se guarda su uso.`
              : clave.status === 'expired'
                ? `Caducó el ${diaMes(clave.expires_at)}: ya no funciona. Se guarda su uso.`
                : soloLectura
                  ? 'El negocio está en solo lectura: no se crean ni se rotan claves. Revocar sí se puede.'
                  : null;

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                titulo={clave.name}
                eyebrow={clave.type === 'test' ? 'Clave de pruebas' : 'Clave privada'}
                subtitulo={`${clave.prefix}… · ${String(clave.permissions.length)} permisos`}
                chip={clave.status === 'active' ? undefined : estado.texto}
                chipEstado={estado.chip}
                icono="key-round"
                accion={!soloLectura && clave.status === 'active' ? 'Rotar' : ''}
                accionIcono="refresh-cw"
                onAccion={() => { setSolape('24'); setModal('rotar'); }}
            >
                <div className="rv-wb-form">
                    <Segmentos etiqueta="Partes de la clave" movil={movil} items={PESTANAS.map((p) => ({ etiqueta: p.texto, icono: p.icono }))} valor={pestana} onCambio={setPestana} />
                    {aviso !== null && (
                        <p className={clave.status === 'rotating' ? 'rv-wb-banda' : 'rv-wb-estado'}>
                            <Icono nombre={clave.status === 'rotating' ? 'refresh-cw' : cerrada ? 'ban' : 'eye'} />
                            <span>{aviso}</span>
                        </p>
                    )}
                    {pestana === 0 && (
                        <>
                            <div className="rv-wb-tarjetas">
                                <div className="rv-wb-tarjeta rv-wb-tarjeta--on">
                                    <strong>Peticiones</strong>
                                    <b>{cifra(peticiones)}</b>
                                    <small>{ventana === 'hour' ? 'Últimas 24 horas' : 'Últimos 14 días'}</small>
                                </div>
                                <div className="rv-wb-tarjeta">
                                    <strong>Frenadas</strong>
                                    <b>{cifra(frenadas)}</b>
                                    <small>Por pasar del límite</small>
                                </div>
                                <div className="rv-wb-tarjeta">
                                    <strong>Último uso</strong>
                                    <b>{clave.last_used_at == null ? '—' : diaYHora(clave.last_used_at)}</b>
                                    <small>{clave.last_used_at == null ? 'Sin usar' : 'Fecha'}</small>
                                </div>
                            </div>
                            <section className="rv-wb-caja rv-wb-dev__uso" aria-label="Peticiones">
                                <div className="rv-wb-dev__titulo">
                                    <span className="rv-ca-fila__texto">
                                        <strong>Peticiones</strong>
                                        {u !== null && <small>{subtituloDelUso(ventana, u.timezone)}</small>}
                                    </span>
                                    <Segmentos
                                        etiqueta="Ventana"
                                        movil={movil}
                                        items={[{ etiqueta: 'Por horas', icono: 'clock' }, { etiqueta: 'Por días', icono: 'calendar' }]}
                                        valor={ventana === 'hour' ? 0 : 1}
                                        onCambio={(i) => { setVentana(i === 0 ? 'hour' : 'day'); }}
                                    />
                                </div>
                                {u === null ? (
                                    <EstadoCargando seccion="el uso" />
                                ) : peticiones === 0 ? (
                                    <small>{`Sin peticiones en este tiempo. ${cerrada ? 'La clave ya no funciona.' : 'Aparecen en cuanto un sistema la use.'}`}</small>
                                ) : (
                                    <div className="rv-wb-barras" role="list">
                                        {u.windows.map((w) => (
                                            <span
                                                key={w.start}
                                                role="listitem"
                                                title={`${inicioDeVentana(w.start, ventana, u.timezone)} · ${cifra(w.requests)} peticiones${w.rate_limited > 0 ? ` · ${cifra(w.rate_limited)} frenadas por el límite` : ''}`}
                                                style={{ height: `${String(Math.round((w.requests / maximo) * 100))}%` }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>
                            <dl className="rv-wb-datos">
                                <div>
                                    <dt>
                                        <Icono nombre="key-round" /> Empieza por
                                    </dt>
                                    <dd className="rv-wb-mono">{`${clave.prefix}…`}</dd>
                                </div>
                                <div>
                                    <dt>
                                        <Icono nombre="shield" /> Tipo
                                    </dt>
                                    <dd>{clave.type === 'test' ? 'De pruebas (negocio de demostración)' : 'Privada, solo de servidor a servidor'}</dd>
                                </div>
                                <div>
                                    <dt>
                                        <Icono nombre="calendar" /> Creada
                                    </dt>
                                    <dd>{`${diaMes(clave.created_at)} · ${autor}`}</dd>
                                </div>
                                <div>
                                    <dt>
                                        <Icono nombre="clock" /> {clave.status === 'rotating' ? 'Deja de funcionar' : clave.status === 'revoked' ? 'Revocada' : 'Caduca'}
                                    </dt>
                                    <dd>{clave.status === 'rotating' ? diaYHora(clave.expires_at) : clave.status === 'revoked' ? (clave.revoked_at == null ? 'Ya no funciona' : `El ${diaMes(clave.revoked_at)}`) : clave.expires_at == null ? 'No caduca' : `El ${diaMes(clave.expires_at)}`}</dd>
                                </div>
                                <div>
                                    <dt>
                                        <Icono nombre="locate-fixed" /> Direcciones IP
                                    </dt>
                                    <dd>{(clave.allowed_ips ?? []).length > 0 ? (clave.allowed_ips ?? []).join(', ') : 'Cualquiera'}</dd>
                                </div>
                            </dl>
                            {!cerrada && (
                                <div className="rv-wb-botones rv-wb-dev__revocar">
                                    <Boton tipo="secundario" rojo icono="ban" onClick={() => { setModal('revocar'); }}>
                                        Revocar la clave
                                    </Boton>
                                    <small>Deja de funcionar en el acto. Hazlo si crees que se ha filtrado.</small>
                                </div>
                            )}
                        </>
                    )}
                    {pestana === 1 && (
                        <>
                            {grupos.map((g) => (
                                <div key={g.modulo} className="rv-wb-dev__grupo">
                                    <strong>{g.nombre}</strong>
                                    {g.permisos.map((p) => (
                                        <div key={p.codigo} className="rv-wb-dev__permiso">
                                            <span>{p.texto}</span>
                                            <span className="rv-wb-mono">{p.codigo}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                            <p className="rv-ca-nota rv-ca-nota--gris">
                                <Icono nombre="lock" /> Los permisos de una clave no se cambian. Para otros, crea otra clave y revoca esta.
                            </p>
                        </>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={modal === 'rotar'}
                titulo={`Rotar «${clave.name}»`}
                texto="Se crea una clave nueva con los mismos permisos. Cámbiala en el sistema que la usa antes de que la antigua deje de funcionar."
                accion="Rotar"
                accionIcono="refresh-cw"
                ocupado={ocupado}
                ocupadoTexto="Rotando…"
                movil={movil}
                onAccion={rotar}
                onCerrar={() => { if (!ocupado) setModal(null); }}
            >
                <Desplegable
                    etiqueta="La antigua sigue funcionando"
                    valor={solape}
                    opciones={SOLAPAMIENTOS}
                    onChange={setSolape}
                    ayuda={solape === '0' ? 'Lo que la use dejará de conectarse en el acto.' : 'Después se revoca sola.'}
                    nombre="rotar-solape"
                />
            </Ventana>
            <Ventana
                abierta={modal === 'revocar'}
                titulo={`¿Revocar «${clave.name}»?`}
                texto={`La clave ${clave.prefix}… deja de funcionar en el acto: el sistema que la use ya no podrá conectarse. No se puede deshacer.`}
                accion="Revocar"
                accionIcono="ban"
                ocupado={ocupado}
                ocupadoTexto="Revocando…"
                movil={movil}
                onAccion={revocar}
                onCerrar={() => { if (!ocupado) setModal(null); }}
            />
        </>
    );
}
